import React from 'react'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import Card from '@/components/common/Card'
import { useAppSelector } from '@/store/store'
import { useNavigate } from 'react-router-dom'
import { RoutesLink } from '@/utility/RouteLinks'
import { Mail, Phone, Shield, MapPin, Briefcase, CreditCard, Heart, User, Pencil } from 'lucide-react'

const Profile: React.FC = () => {
    const user = useAppSelector((state) => state.user.user);
    const navigate = useNavigate();

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title="My Profile"
                    buttonText="Edit Profile"
                    buttonIcon={<Pencil className="w-4 h-4" />}

                    onClick={() => navigate(RoutesLink.editProfile)}
                />

                {/* Profile Header Card */}
                <Card>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                        <div className="relative">
                            {user?.profilePhoto ? (
                                <img
                                    src={user.profilePhoto as string}
                                    alt={user?.name}
                                    className="w-24 h-24 rounded-2xl object-cover border-2 border-primary/20 shadow-lg"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/20 flex items-center justify-center shadow-lg">
                                    <span className="text-3xl font-bold text-primary">
                                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                    </span>
                                </div>
                            )}
                            <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white shadow-sm ${user?.status === 'current' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-2xl font-bold text-foreground">{user?.name || '—'}</h2>
                            <p className="text-sm text-muted-foreground mt-0.5">{user?.designationName || '—'}</p>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Mail className="w-3.5 h-3.5 text-primary/70" /> {user?.email || '—'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Phone className="w-3.5 h-3.5 text-primary/70" /> {user?.phone || '—'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    {/* <Shield className="w-3.5 h-3.5 text-primary/70" /> {user?.role?.name || user?.type || '—'} */}
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase
                                ${user?.status === 'current'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-slate-50 text-slate-600 border border-slate-200'
                                }`}>
                                {user?.status || '—'}
                            </span>
                            <span className="text-xs text-muted-foreground">{user?.employeeCode || ''}</span>
                        </div>
                    </div>
                </Card>

                {/* Personal Information */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <User className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Personal Information</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Full Name" value={user?.name} />
                        <InfoItem label="Email" value={user?.email} />
                        <InfoItem label="Phone" value={user?.phone} />
                        <InfoItem label="Gender" value={user?.gender === 0 ? 'Male' : 'Female'} />
                        <InfoItem label="Date of Birth" value={formatDate(user?.dob)} />
                        <InfoItem label="Marital Status" value={user?.isMarried ? 'Married' : 'Unmarried'} />
                        <InfoItem label="Father's Name" value={user?.userDetail?.fatherName} />
                        <InfoItem label="Mother's Name" value={user?.userDetail?.motherName} />
                        <InfoItem label="Blood Group" value={user?.bloodGroup} />
                    </div>
                </Card>

                {/* Employment Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Briefcase className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Employment Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Employee Code" value={user?.employeeCode} />
                        <InfoItem label="Department" value={user?.department?.name || "-"} />
                        <InfoItem label="Designation" value={user?.designation?.name || "-"} />
                        <InfoItem label="Site" value={user?.site?.name || "-"} />
                        <InfoItem label="Joining Date" value={formatDate(user?.joiningDate)} />
                        <InfoItem label="Status" value={user?.status} />
                    </div>
                </Card>

                {/* Emergency Contact */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Phone className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Emergency Contact</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Contact Person" value={user?.userDetail?.ecpName} />
                        <InfoItem label="Contact Number" value={user?.userDetail?.ecpPhone} />
                    </div>
                </Card>

                {/* Address Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <MapPin className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Address Details</h3>
                    </div>

                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Present Address</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5 mb-6">
                        <InfoItem label="Address" value={user?.userDetail?.address} />
                        <InfoItem label="State" value={user?.userDetail?.state} />
                        <InfoItem label="City" value={user?.userDetail?.city} />
                        <InfoItem label="Pin Code" value={user?.userDetail?.pinCode} />
                    </div>

                    <div className="border-t border-border/50 pt-5">
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                            Permanent Address
                            {user?.userDetail?.isSameAsPermanentAddress && (
                                <span className="ml-2 text-xs font-normal text-primary">(Same as present)</span>
                            )}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                            <InfoItem label="Address" value={user?.userDetail?.permanentAddress} />
                            <InfoItem label="State" value={user?.userDetail?.permanentState} />
                            <InfoItem label="City" value={user?.userDetail?.permanentCity} />
                            <InfoItem label="Pin Code" value={user?.userDetail?.permanentPinCode} />
                        </div>
                    </div>
                </Card>

                {/* ID & Documents */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Shield className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">ID & Documents</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Aadhar Number" value={user?.userDetail?.aadharNumber} />
                        <InfoItem label="PAN Number" value={user?.userDetail?.panNumber} />
                        <InfoItem label="UAN Number" value={user?.userDetail?.uanNumber} />
                        <InfoItem label="ESIC Number" value={user?.userDetail?.esicNumber} />
                        <InfoItem label="PF Number" value={user?.userDetail?.pfNumber} />
                        <InfoItem label="Insurance Number" value={user?.userDetail?.insuranceNumber} />
                        <InfoItem label="Driving License" value={user?.userDetail?.drivingLicenseNumber} />
                    </div>
                </Card>

                {/* Bank Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <CreditCard className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Bank Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Bank Name" value={user?.userDetail?.bankName} />
                        <InfoItem label="Account Number" value={user?.userDetail?.accountNumber} />
                        <InfoItem label="Account Holder" value={user?.userDetail?.accountHolderName} />
                        <InfoItem label="IFSC/RTGS Code" value={user?.userDetail?.ifscCode} />
                        <InfoItem label="Branch Address" value={user?.userDetail?.branchAddress} />
                    </div>
                </Card>

                {/* Nominee Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Heart className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Nominee Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                        <InfoItem label="Nominee Name" value={user?.userDetail?.nomineeName} />
                        <InfoItem label="Relation" value={user?.userDetail?.nomineeRelation} />
                        <InfoItem label="Phone" value={user?.userDetail?.nomineePhone} />
                        <InfoItem label="Date of Birth" value={(user?.userDetail?.nomineeDob)} />
                    </div>
                </Card>
            </div>
        </Layout>
    )
}

/** Reusable info display item */
const InfoItem: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
    <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-medium text-foreground">{value || '—'}</p>
    </div>
);

export default Profile
